import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  lightboxHandleBarRecipe,
  lightboxHandleRecipe,
  lightboxImageRecipe,
  lightboxPanelRecipe,
  lightboxSheetRecipe,
  lightboxStageRecipe,
  type LightboxSheetRecipeProps,
  type LightboxStageRecipeProps,
} from '../recipes/lightbox.recipe';

export interface LightboxSheetProps
  extends React.HTMLAttributes<HTMLElement>,
    LightboxSheetRecipeProps {
  /** Draw the drag bar across the top on a phone. */
  handle?: boolean;
}

/**
 * The photo viewer's frame: a sheet rising from the bottom of the screen,
 * holding a `LightboxStage` and a `LightboxPanel` side by side.
 *
 *   <DialogPrimitive.Content asChild>
 *     <LightboxSheet handle height={fullHeight ? 'full' : 'partial'}>
 *       <LightboxStage src={url} alt="Photo 3">…overlay controls…</LightboxStage>
 *       <LightboxPanel>…comments…</LightboxPanel>
 *     </LightboxSheet>
 *   </DialogPrimitive.Content>
 *
 * Rendered as an `aside`: the open/close animation is driven by the dialog
 * primitive's `data-state`, which arrives through `asChild`, so the element
 * forwards its ref and spreads whatever it is given.
 */
export const LightboxSheet = React.forwardRef<HTMLElement, LightboxSheetProps>(
  function LightboxSheet(
    { height, handle = false, className, children, ...props },
    ref
  ) {
    const asideRef = ref as React.Ref<HTMLElement>;
    return (
      <aside
        ref={asideRef}
        className={cn(lightboxSheetRecipe({ height }), className)}
        {...props}
      >
        {handle ? (
          <span className={lightboxHandleRecipe()} aria-hidden="true">
            <span className={lightboxHandleBarRecipe()} />
          </span>
        ) : null}
        {children}
      </aside>
    );
  }
);

export interface LightboxStageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    LightboxStageRecipeProps {
  src: string;
  alt: string;
  /** Laid over the picture: close, delete, the page counter. Position them. */
  children?: React.ReactNode;
}

/**
 * The dark stage the picture sits on, with room for controls over it.
 * `layout="alone"` when there is no panel beside it, so it fills the sheet.
 */
export const LightboxStage = React.forwardRef<
  HTMLDivElement,
  LightboxStageProps
>(function LightboxStage(
  { src, alt, layout, className, children, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(lightboxStageRecipe({ layout }), className)}
      {...props}
    >
      <img src={src} alt={alt} className={lightboxImageRecipe()} />
      {children}
    </div>
  );
});

export type LightboxPanelProps = React.HTMLAttributes<HTMLDivElement>;

/** The activity column beside the stage — a column; fill it with Stacks. */
export const LightboxPanel = React.forwardRef<
  HTMLDivElement,
  LightboxPanelProps
>(function LightboxPanel({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(lightboxPanelRecipe(), className)}
      {...props}
    />
  );
});
